import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { Router } from '@angular/router';

import { SessionService } from '@rpgc-ai/services/auth/session.service';
import { type ModalOptions, ModalService } from '@rpgc-ai/services/modal/modal.service';
import { EmailVerificationService } from '@rpgc-ai/services/profile/email-verification.service';
import { ProfileService } from '@rpgc-ai/services/profile/profile.service';
import { StorageService } from '@rpgc-ai/services/storage/storage.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import { EXCLUDED_PATHS } from '@rpgc-ai/shared-interfaces/consts/age-confirm-guard-exceptions.const';
import { EModalName } from '@rpgc-ai/shared-interfaces/enums/modal-name.enum';
import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';

const AGE_CONFIRMATION_MODAL = 'age-confirmation';
const AGE_CONFIRMATION_MODAL_OPTIONS: ModalOptions = {
  disableOverlayClose: true,
  allowStack: true,
};
const EMAIL_VERIFICATION_REQUIRED_PATHS = new Set([
  '/blocked-creators',
  '/chat-history',
  '/create-chatbot',
  '/favorites',
  '/my-chatbots',
  '/my-personas',
]);

function isEmailVerificationRequiredUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0].replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';

  return EMAIL_VERIFICATION_REQUIRED_PATHS.has(path) || path.startsWith('/chat/') || path.startsWith('/view-chatbot/');
}

let forceNavigationFromGuard = false;

function hardNavGuard(router: Router, lang: string, stateUrl: string): UrlTree | boolean {
  const isHardNav = router.url !== stateUrl && router.url === '/';

  if (isHardNav) {
    forceNavigationFromGuard = true;
    return router.createUrlTree([lang]);
  } else {
    return false;
  }
}

/**
 * Пока профиль с сервера не известен, нельзя пускать на маршруты с tosGuard — иначе обход ToS/adult.
 */
async function waitForAuthenticatedUserProfile(
  profileStore: {
    profile: () => { id?: string | null };
    isLoading: () => boolean;
    error: () => string | null;
  },
  profileService: ProfileService,
): Promise<boolean> {
  if (profileStore.profile()?.id) {
    return true;
  }

  profileService.getProfile();

  const deadline = Date.now() + 15_000;
  let sawLoading = false;

  while (Date.now() < deadline) {
    const p = profileStore.profile();
    if (p?.id) {
      return true;
    }
    if (profileStore.error()) {
      return false;
    }
    if (profileStore.isLoading()) {
      sawLoading = true;
    } else if (sawLoading) {
      return false;
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 25);
    });
  }

  return !!profileStore.profile()?.id;
}

/**
 * Guard для защиты маршрутов, требующих авторизации
 * Если пользователь не авторизован, перенаправляет на страницу входа
 */
export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authStore = inject(AuthStore);
  const profileStore = inject(ProfileStore);
  const sessionService = inject(SessionService);
  const router = inject(Router);
  const modalService = inject(ModalService);
  const storageService = inject(StorageService);
  const languageService = inject(LanguageService);
  const isServer = inject(IS_SERVER);

  // skip for ssr
  if (isServer) {
    return true;
  }
  // 1) Уже авторизован в сторе
  if (authStore.isAuthenticated()) {
    return true;
  }
  // 2) Статус из SessionService уже известен (например, из TransferState)
  const ssrAuth = sessionService.isAuthenticated();
  if (ssrAuth === true) {
    return true;
  }

  // 3) Если статус ещё не определён, пробуем один раз загрузить сессию (SSR использует REQUEST headers)
  if (ssrAuth === undefined) {
    try {
      const data = await sessionService.getSession();
      if (data) {
        return true;
      }
    } catch {
      // ignore errors, fallback to redirect
    }
  }

  // 4) Не авторизован — открываем модалку входа и редиректим на корень языка
  const lang = languageService.getCurrentLanguage();
  const { isAdultConfirmed } = profileStore.profile() ?? {};
  const fromBackend = authStore.isAuthenticated() && isAdultConfirmed;
  const fromStorage = storageService.isAdultConfirmedValid();
  const isAgeConfirmed = fromBackend || fromStorage;

  if (!isAgeConfirmed) {
    if (!modalService.getModalStack().includes(AGE_CONFIRMATION_MODAL)) {
      await modalService.openModal(AGE_CONFIRMATION_MODAL, [], AGE_CONFIRMATION_MODAL_OPTIONS);
    }
    return router.createUrlTree([lang]);
  }

  void modalService.openModal(EModalName.SIGN_IN);
  return false;
  // return router.createUrlTree([lang]);
};

/**
 * Инвертированный guard для защиты маршрутов, требующих авторизации
 */
export const authGuardInverted: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const languageService = inject(LanguageService);

  // Статус из SessionService уже известен
  const ssrAuth = sessionService.isAuthenticated();

  if (ssrAuth === false) {
    return true; // Не авторизован - пропускаем
  }

  // Если статус ещё не определён, пробуем загрузить сессию
  if (ssrAuth === undefined) {
    try {
      const data = await sessionService.getSession();
      if (!data) {
        return true; // Сессия не найдена - пропускаем
      }
    } catch {
      return true; // При ошибке считаем неавторизованным
    }
  }

  // Пользователь авторизован - редиректим на главную языка
  const currentUrl = state.url;
  const langMatch = currentUrl.match(/^\/([a-z]{2})(\/|$)/);
  const lang = langMatch ? langMatch[1] : languageService.getCurrentLanguage() || 'en';

  return router.createUrlTree([lang]);
};

export const tosGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);

  const profileStore = inject(ProfileStore);
  const authStore = inject(AuthStore);
  const sessionService = inject(SessionService);
  const profileService = inject(ProfileService);
  const modalService = inject(ModalService);
  const emailVerificationService = inject(EmailVerificationService);
  const languageService = inject(LanguageService);

  const isServer = inject(IS_SERVER);
  const lang = languageService.getCurrentLanguage();

  // SSR: always pass — modal/redirect logic is client-only
  if (isServer) {
    return true;
  }

  const sessionActive = authStore.isAuthenticated() || sessionService.isAuthenticated() === true;

  if (forceNavigationFromGuard) {
    forceNavigationFromGuard = false;
    return true;
  }

  if (!sessionActive) {
    return true;
  }

  const profileOk = await waitForAuthenticatedUserProfile(profileStore, profileService);
  if (!profileOk) {
    return false;
  }

  const { isAdultConfirmed, isTermsAccepted, id, emailVerified } = profileStore.profile() ?? {};

  if (!!id && (!isAdultConfirmed || !isTermsAccepted)) {
    await modalService.openModal(EModalName.GUIDELINES);
    return hardNavGuard(router, lang, state.url);
  }
  if (emailVerificationService.secondsLeft() > 59) {
    return false;
  }

  if (!!id && !emailVerified) {
    if (!isEmailVerificationRequiredUrl(state.url) || !emailVerificationService.shouldOpenMandatoryModal(state.url, router.url)) {
      return true;
    }
    emailVerificationService.markMandatoryModalOpened();
    void modalService.openModal(EModalName.EMAIL_VERIFICATION, ['confirm', 'mandatory'], { disableOverlayClose: true });
    return hardNavGuard(router, lang, state.url);
  }

  return true;
};

export const AgeConfirmationGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const profileStore = inject(ProfileStore);
  const authStore = inject(AuthStore);

  const storageService = inject(StorageService);
  const modalService = inject(ModalService);

  const isServer = inject(IS_SERVER);
  const router = inject(Router);

  if (isServer) {
    return true;
  }

  const modalStack = modalService.getModalStack();

  if (modalStack.includes(AGE_CONFIRMATION_MODAL) || modalStack.includes('privacy-policy') || modalStack.includes('terms-and-conditions')) {
    return true;
  }

  if (EXCLUDED_PATHS.some((path) => state.url.includes(path))) {
    return true;
  }

  const { isAdultConfirmed } = profileStore.profile() ?? {};
  const isAuthenticated = authStore.isAuthenticated();
  const fromBackend = isAuthenticated && isAdultConfirmed;
  const fromStorage = storageService.isAdultConfirmedValid();

  if (!fromBackend && !fromStorage) {
    await modalService.openModal(AGE_CONFIRMATION_MODAL, [], AGE_CONFIRMATION_MODAL_OPTIONS);
    return router.parseUrl(state.url);
  }

  return true;
};
